import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FiFileText, FiShield, FiDollarSign, FiTruck, FiAlertCircle, FiHome } from 'react-icons/fi';

const TermsAndConditions = () => {
  const sections = [
    { id: 'deposits', title: 'Deposits & Payments', icon: FiDollarSign },
    { id: 'delivery', title: 'Delivery & Setup', icon: FiTruck },
    { id: 'warranty', title: 'Warranty', icon: FiShield },
    { id: 'cancellations', title: 'Cancellations & Refunds', icon: FiAlertCircle },
    { id: 'site-prep', title: 'Site Preparation', icon: FiHome },
    { id: 'general', title: 'General Terms', icon: FiFileText },
  ];

  return (
    <>
      <Helmet>
        <title>Terms and Conditions - ShedX Shed Marketplace</title>
        <meta name="description" content="Review ShedX terms covering shed deposits, delivery, warranty, cancellation and refund policies before purchasing." />
      </Helmet>

      {/* Hero */}
      <section className="bg-gray-950 text-white py-20 pt-28">
        <div className="container mx-auto px-4 text-center">
          <FiFileText className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Terms and Conditions</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Review our terms for purchasing, delivery and warranty of storage sheds and outdoor buildings through the ShedX marketplace.
          </p>
          <p className="text-sm text-gray-500 mt-4">Last updated: February 2026</p>
        </div>
      </section>

      {/* Quick Nav */}
      <section className="bg-white border-b border-gray-200 py-4 sticky top-16 z-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded hover:border-gray-400 transition-colors"
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl space-y-12">

          {/* 1. Deposits & Payments */}
          <div id="deposits">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiDollarSign className="w-6 h-6 text-emerald-600" />
              1. Deposits &amp; Payments
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1.1 Deposit Requirement</h3>
                <p>A 25% non-refundable deposit is required to reserve your shed and begin the ordering or delivery process. The remaining 75% balance is due upon delivery and setup completion.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1.2 Accepted Payment Methods</h3>
                <p>We accept major credit cards, debit cards and ACH bank transfers. All payments are processed securely through our payment processor.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1.3 Rent-to-Own (RTO)</h3>
                <p>RTO agreements are separate contracts between the customer and the RTO provider. ShedX facilitates the connection but is not a party to the RTO agreement. Terms, rates and approval are set by the RTO provider. No credit check is required for most RTO programs.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1.4 Pricing</h3>
                <p>Prices shown on ShedX are set by individual dealers and are subject to change without notice. Prices exclude applicable taxes. Written quotes are valid for 7 days unless otherwise stated.</p>
              </div>
            </div>
          </div>

          {/* 2. Delivery & Setup */}
          <div id="delivery">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiTruck className="w-6 h-6 text-emerald-600" />
              2. Delivery &amp; Setup
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.1 Free Delivery</h3>
                <p>Free delivery is included within 50 miles of the dealer location on most models. Deliveries beyond 50 miles incur a fee of $3 per additional mile. Exact delivery cost will be confirmed before scheduling.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.2 Delivery Timeline</h3>
                <p>Most in-stock sheds deliver within 5-10 business days. Custom orders may take 4-8 weeks depending on the builder. Delivery dates are estimates, not guarantees. ShedX is not liable for delays caused by weather, manufacturer backlog or transportation issues.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.3 Setup &amp; Installation</h3>
                <p>Professional setup is included with most shed purchases. Setup includes leveling the shed on your prepared site and securing all components. The delivery crew will place the shed in the location you designate, provided the path is clear and accessible.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.4 Delivery Access</h3>
                <p>Customer must ensure a clear delivery path at least 2 feet wider than the shed. The ground must support the weight of the delivery vehicle. Overhead obstructions (power lines, tree branches) must be cleared before delivery. Sheds cannot be delivered in wet conditions that prevent safe truck access.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.5 Damage During Delivery</h3>
                <p>Inspect your shed upon delivery. Report any damage to the delivery crew immediately and note it on the delivery receipt. Contact ShedX within 48 hours with photos of any damage. Claims filed after 48 hours may not be honored.</p>
              </div>
            </div>
          </div>

          {/* 3. Warranty */}
          <div id="warranty">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiShield className="w-6 h-6 text-emerald-600" />
              3. Warranty
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3.1 Manufacturer Warranty</h3>
                <p>All new sheds include a manufacturer warranty. Warranty duration and coverage vary by builder and model (typically 5-10 years). Warranty details are provided with your purchase documentation.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3.2 Coverage</h3>
                <p>Warranties generally cover defects in materials and workmanship, including structural integrity, roof leaks under normal conditions, siding defects and door/window hardware failures.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3.3 Exclusions</h3>
                <p>Warranties do not cover damage from natural disasters, flooding, wind above rated speeds, improper site preparation, unauthorized modifications, normal wear and tear or failure to perform routine maintenance. Customer must register their warranty within 30 days of delivery.</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-4">
                <p className="text-sm text-amber-800">
                  <strong>ShedX acts as a marketplace.</strong> Warranty claims are handled directly between the customer and the manufacturer/dealer. ShedX will assist in facilitating communication but does not independently warrant any products.
                </p>
              </div>
            </div>
          </div>

          {/* 4. Cancellations & Refunds */}
          <div id="cancellations">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiAlertCircle className="w-6 h-6 text-emerald-600" />
              4. Cancellations &amp; Refunds
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.1 In-Stock Sheds</h3>
                <p>Cancellations within 24 hours of deposit may be eligible for a deposit credit toward a future purchase. After 24 hours, deposits are non-refundable.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.2 Custom Orders</h3>
                <p>Custom-built sheds cannot be canceled once production has begun. No refunds will be issued on custom orders after the build starts.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.3 Refund Processing</h3>
                <p>Approved refunds are processed within 5-10 business days to the original payment method. Credit card refunds may take an additional billing cycle to appear on your statement.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.4 Order Modifications</h3>
                <p>Changes to shed orders (size, color, features) must be requested in writing within 24 hours of placing the deposit. Modifications after this window may incur additional charges or require a new order.</p>
              </div>
            </div>
          </div>

          {/* 5. Site Preparation */}
          <div id="site-prep">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiHome className="w-6 h-6 text-emerald-600" />
              5. Site Preparation
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5.1 Customer Responsibility</h3>
                <p>The customer is solely responsible for preparing the shed site before delivery. This includes:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Level ground (within 3 inches across the entire pad area)</li>
                  <li>Gravel, concrete or compacted base as appropriate</li>
                  <li>Adequate drainage away from the shed location</li>
                  <li>Compliance with local setback and zoning requirements</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5.2 Permits &amp; HOA Approval</h3>
                <p>The customer is responsible for obtaining all required building permits and HOA approvals before delivery. ShedX and its dealers are not liable for permit violations, fines or required removal of a shed due to code issues.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5.3 Failed Delivery Attempts</h3>
                <p>If the delivery crew arrives and the site is not prepared (unlevel, inaccessible, or obstructed), a re-delivery fee may apply. The customer will be charged for each failed delivery attempt.</p>
              </div>
            </div>
          </div>

          {/* 6. General Terms */}
          <div id="general">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiFileText className="w-6 h-6 text-emerald-600" />
              6. General Terms
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.1 Marketplace Role</h3>
                <p>ShedX operates as an online marketplace connecting shed buyers with licensed dealers and manufacturers. ShedX is not the seller, builder or manufacturer of any shed listed on the platform. Individual dealers are responsible for inventory accuracy, pricing, construction quality and fulfillment.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.2 Limitation of Liability</h3>
                <p>ShedX's total liability is limited to the platform fees collected. ShedX is not liable for indirect, incidental, special or consequential damages. ShedX does not guarantee shed quality, delivery timelines or dealer performance.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.3 Accuracy of Listings</h3>
                <p>Shed photos, dimensions and specifications are provided by dealers. While we strive for accuracy, variations may occur. Colors shown on screen may differ from actual shed colors. Contact the dealer to confirm specifications before purchasing.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.4 Governing Law</h3>
                <p>These terms are governed by and construed under the laws of the State of Texas. Any disputes shall be resolved through binding arbitration in the State of Texas.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.5 Modifications</h3>
                <p>ShedX reserves the right to modify these terms at any time. Changes take effect immediately upon posting. Continued use of the platform after changes constitutes acceptance of the updated terms.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.6 Privacy</h3>
                <p>Your use of ShedX is also governed by our <Link to="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</Link>.</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-900 text-white rounded-lg p-8 text-center">
            <h2 className="text-xl font-bold mb-2">Questions about these terms?</h2>
            <p className="text-gray-400 mb-6">Reach out and we'll clarify anything before you buy.</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </Link>
          </div>

        </div>
      </section>
    </>
  );
};

export default TermsAndConditions;
